import { useEffect, useState } from 'react';
import { Alert, Badge, Box, Button, ColumnLayout, Modal, SpaceBetween, Spinner, Table, type TableProps } from '@cloudscape-design/components';
import type { ApiClient } from '@deloitte-us-consulting/gp-sdk';
import {
  changeText,
  formatCount,
  formatPerPerson,
  peoplePath,
  type Metrics,
  type PeriodRef,
  type UsagePerson,
} from './usageApi';
import { ChangeText } from './ChangeText';
import { byNumber, byText, useTableSort } from './useTableSort';

export interface DrilldownTarget {
  target: string;
  name: string;
  removed: boolean;
  current: Metrics;
  previous: Metrics;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

/** One target's numbers for the selected period, and the people behind them. */
export function TargetDrilldown({
  row,
  period,
  api,
  teamVersion,
  adding,
  onAdd,
  onDismiss,
}: {
  row: DrilldownTarget;
  period: PeriodRef;
  api: Pick<ApiClient, 'get'>;
  /** Changes whenever the platform team is saved, so the Platform team badges refresh. */
  teamVersion: string;
  adding: boolean;
  onAdd: (person: UsagePerson) => void;
  onDismiss: () => void;
}) {
  const [people, setPeople] = useState<UsagePerson[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const data = await api.get<UsagePerson[]>(peoplePath(row.target, period));
        if (!cancelled) setPeople(data);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, row.target, period, teamVersion]);

  const stats: { label: string; value: string; cur: number; prev: number }[] = [
    { label: 'Unique people', value: formatCount(row.current.uniques), cur: row.current.uniques, prev: row.previous.uniques },
    { label: 'Accesses', value: formatCount(row.current.accesses), cur: row.current.accesses, prev: row.previous.accesses },
    { label: 'Accesses per person', value: formatPerPerson(row.current.perPerson), cur: row.current.perPerson, prev: row.previous.perPerson },
    { label: 'Regulars', value: formatCount(row.current.regulars), cur: row.current.regulars, prev: row.previous.regulars },
  ];

  const columns: TableProps.ColumnDefinition<UsagePerson>[] = [
    {
      id: 'person',
      header: 'Person',
      sortingField: 'person',
      sortingComparator: byText((p) => p.name ?? p.email),
      cell: (p) => (
        <div>
          {p.name ?? p.email}
          {p.name && <Box variant="small" color="text-body-secondary">{p.email}</Box>}
        </div>
      ),
    },
    { id: 'accesses', header: 'Accesses', sortingField: 'accesses', sortingComparator: byNumber((p) => p.accesses), cell: (p) => formatCount(p.accesses) },
    { id: 'days', header: 'Days active', sortingField: 'days', sortingComparator: byNumber((p) => p.daysActive), cell: (p) => formatCount(p.daysActive) },
    { id: 'last', header: 'Last visit', sortingField: 'last', sortingComparator: byText((p) => p.lastAt), cell: (p) => formatDate(p.lastAt) },
    {
      id: 'team',
      header: 'Platform team',
      sortingField: 'team',
      sortingComparator: byNumber((p) => (p.onTeam ? 1 : 0)),
      cell: (p) =>
        p.onTeam ? (
          <Badge color="blue">Platform team</Badge>
        ) : (
          <Button
            variant="inline-link"
            disabled={adding || !p.email}
            ariaLabel={`Add ${p.name ?? p.email} to platform team`}
            onClick={() => onAdd(p)}
          >
            Add to platform team
          </Button>
        ),
    },
  ];
  // The server sends people most accesses first; show that as the starting sort.
  const sort = useTableSort(people ?? [], columns, { field: 'accesses', descending: true });

  return (
    <Modal
      visible
      size="large"
      onDismiss={onDismiss}
      header={`${row.name} · ${period.label}`}
      footer={
        <Box float="right">
          <Button onClick={onDismiss}>Close</Button>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {row.removed && <Badge color="grey">No longer in the registry</Badge>}
        <ColumnLayout columns={4} variant="text-grid">
          {stats.map((s) => (
            <div key={s.label}>
              <Box variant="awsui-key-label">{s.label}</Box>
              <Box variant="h2">{s.value}</Box>
              <ChangeText change={changeText(s.cur, s.prev)} soFar={!period.closed} />
            </div>
          ))}
        </ColumnLayout>
        {error && <Alert type="error" header="Could not load people">{error}</Alert>}
        <Table
          variant="embedded"
          {...sort}
          loading={people === null && !error}
          loadingText="Loading people"
          trackBy="sub"
          empty={<Box color="text-body-secondary" textAlign="center">No visits recorded for this period.</Box>}
        />
        {people === null && !error && <Spinner />}
      </SpaceBetween>
    </Modal>
  );
}
